import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useFgp } from "@/context/FgpContext";
import { buildBrief } from "@/lib/brief";
import { Sparkles, AlertTriangle, Calendar, FileText, CheckCircle2, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const iconMap = { alert: AlertTriangle, calendar: Calendar, doc: FileText, check: CheckCircle2 };
const toneMap = {
  critical: "text-rose-600 bg-rose-50",
  warning: "text-amber-600 bg-amber-50",
  good: "text-emerald-600 bg-emerald-50",
};

export const AiBriefCard = () => {
  const { currentUser, events, tasks, renewals, documents } = useFgp();
  const brief = useMemo(() => buildBrief({ currentUser, events, tasks, renewals, documents }), [currentUser, events, tasks, renewals, documents]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl border border-neutral-200 bg-gradient-to-br from-white via-white to-neutral-50/60 shadow-sm"
      data-testid="ai-daily-brief"
    >
      <div className="absolute top-0 right-0 h-40 w-40 bg-neutral-100/40 rounded-full blur-3xl -mr-10 -mt-10" />
      <div className="relative p-6 sm:p-8">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-neutral-600 text-white flex items-center justify-center shadow-sm shadow-neutral-600/30">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <p className="font-heading text-lg font-semibold text-slate-900">{brief.greeting}</p>
              <p className="text-xs text-slate-400">{brief.dateLabel}</p>
            </div>
          </div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-600 bg-neutral-100 px-2.5 py-1 rounded-full">AI Daily Brief</span>
        </div>

        <p className="text-slate-600 leading-relaxed max-w-2xl" data-testid="brief-summary">{brief.summary}</p>

        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          {brief.highlights.map((h, i) => {
            const Icon = iconMap[h.icon] || Sparkles;
            return (
              <div key={i} className="flex items-start gap-3 rounded-xl bg-white/70 border border-slate-200/80 px-3.5 py-3" data-testid={`brief-highlight-${i}`}>
                <span className={`h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0 ${toneMap[h.tone]}`}>
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <span className="text-sm text-slate-700 leading-snug">{h.text}</span>
              </div>
            );
          })}
        </div>

        {brief.focus.length > 0 && (
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider mr-1">Today's focus:</span>
            {brief.focus.map((f) => (
              <span key={f} className="inline-flex items-center gap-1 text-xs font-medium text-neutral-700 bg-neutral-50 border border-neutral-100 px-2.5 py-1 rounded-full">
                <ArrowRight className="h-3 w-3" /> {f}
              </span>
            ))}
          </div>
        )}

        <div className="mt-5 flex items-center justify-between">
          <p className="text-xs text-slate-400">Live summary from your workspace · review before acting.</p>
          <Link to="/app/brief" data-testid="brief-view-full" className="text-sm font-medium text-neutral-700 hover:text-neutral-900 inline-flex items-center gap-1">
            View full brief <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};
